import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from 'components/Utils/NewIcon';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import './style.scss';
import DocumentDetails from 'components/HealthRecords/Documents/DocumentDetails';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { setFormValues } from 'hooks/useReduxForm';
import { api } from 'careand-redux/api/ApiThunks';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { useAppSelector } from 'careand-redux/store';
import NewReferral from 'components/HealthRecords/Referrals/NewReferral';
import CPPUtils from 'services/CPPUtils';
import SignDocument from 'components/HealthRecords/Documents/SignDocument';
import Flow from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/Flow';
import SendMultipleDocuments from 'components/HealthRecords/Documents/SendMultipleDocuments';
import DocumentTemplatesSection from 'components/Patient/CPP/DocumentTemplatesSection';
import { ActivePhysician, IsTypeNurse } from 'careand-redux/selectors';
import { DocumentTemplateModal } from 'components/HealthRecords/Documents/DocumentDetails/view';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import Graph from 'components/HealthRecords/Graph';
import { NewReferralRequest } from 'scenes/HomeStaff/ReferralsDirectory/ReferralRequest';
import PediatricDoseCalculator from 'components/Modals/PediatricDoseCalculatorModal';
import StopBangCalculatorModal from 'components/Modals/StopBangCalculatorModal';
import PregnancyCalculatorModal from 'components/Modals/PregnancyCalculatorModal';
import PREVENTCalculatorModal from 'components/Modals/PREVENTCalculatorModal';
import FIB4CalculatorModal from 'components/Modals/FIB4CalculatorModal';
import LabPriceCalculator from 'components/Misc/LabPriceCalculator';
import PackYearCalculatorModal from 'components/Modals/PackYearCalculatorModal';
import AlcoholCalculatorModal from 'components/Modals/AlcoholCalculatorModal';
import { HandoverDetails } from 'components/Schedule/Vacations/Handovers';
import RouteUtils from '../../../../services/RouteUtils';
import { usePathParams } from '../../../Navigation/NavStack';
import { useNavStack } from '../../../../careand-redux/slices/ui/hooks';
import { Stacks } from '../../../../careand-redux/slices/ui/navigation';
import DefaultModal from '../../../Utils/DefaultModal';
import NewPrescription from '../../../HealthRecords/Prescriptions/NewPrescription';
import IVFCalculatorModal from 'components/Modals/IVFCalculatorModal';
import BMICalculatorModal from 'components/Modals/BMICalculatorModal';
import CrClCalculatorModal from 'components/Modals/CrClCalculatorModal';

const MODES = {
  normal: { value: 'normal', label: 'Normal' },
  sideBySide: { value: 'sideBySide', label: 'Side by Side' },
};

const DEFAULT_ARRAY = [];

const CPPSplitPanel = ({ pinned = DEFAULT_ARRAY, patient, bubble }) => {
  const dispatch = useAppDispatch();

  const [selectedId, setSelectedId] = useState(pinned[0]?.id);
  const [number, setNumber] = useState(pinned.length);
  const [mode, setMode] = useState(MODES.normal.value);

  useEffect(() => {
    let newId = selectedId;

    if (pinned.length > number) {
      newId = pinned[0]?.id;
    }

    let found = pinned.find((document) => document.id === newId);
    if (!found) newId = pinned[0]?.id;

    setSelectedId(newId);
    setNumber(pinned.length);

    window.dispatchEvent(new Event('resize'));
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 500);
  }, [pinned, selectedId, number]);

  if (!pinned || pinned.length === 0) return null;

  function close(id) {
    let pinnedData = pinned.find((data) => data.id === id);
    if (pinnedData?.type === 'sign_document') {
      dispatch(uiActions.removePinnedDocument({ patientId: patient.id, id }));
      dispatch(
        uiActions.addPinnedDocument({
          id: pinnedData.payload.document.id,
          type: 'document',
          patientId: pinnedData.payload.document.patient_id,
        }),
      );
      setSelectedId(pinnedData.payload.document.id);
    } else {
      dispatch(uiActions.removePinnedDocument({ patientId: patient.id, id }));
      if (selectedId === id) setSelectedId(undefined);
    }
  }

  function toggleMode() {
    if (mode === MODES.normal.value) setMode(MODES.sideBySide.value);
    else setMode(MODES.normal.value);
  }

  let className = 'cpp-split-panel full-height';
  className += ` ${mode}`;

  const modeIcon =
    mode === MODES.normal.value ? 'rectangle-vertical-history' : 'rectangle-vertical';

  const sideBySideWidth = 100 / pinned.length;

  const pinnedStyle = mode === MODES.normal.value ? undefined : { width: `${sideBySideWidth}%` };

  return (
    <div className={className}>
      <div className="tabs">
        <div className="controls">
          <Icon
            className="white"
            src={modeIcon}
            alt="Side-by-Side Mode"
            showTooltip={true}
            onClick={toggleMode}
            height="15px"
          />
        </div>
        {pinned.map((pinnedDoc) => {
          return (
            <PinnedTab
              allPinned={pinned}
              pinnedDocument={pinnedDoc}
              selectedId={selectedId}
              close={close}
              setSelectedId={setSelectedId}
            />
          );
        })}
      </div>
      <div className="content full-height full-width tab-content">
        {pinned.map((pinnedDoc) => {
          const { id, type } = pinnedDoc;

          if (type === 'prescription') {
            return (
              <PrescriptionSplit
                visible={id === selectedId}
                key={type}
                prescription={pinnedDoc.prescription}
                patientId={patient.id}
                edit
              />
            );
          }

          if (type === 'reqs_list')
            return (
              <ReqsListRSplit
                key={type}
                pinnedDocument={pinnedDoc}
                visible={id === selectedId}
                patient={patient}
                style={pinnedStyle}
              />
            );

          if (type === 'document')
            return (
              <DocumentPDFSplit
                key={id}
                visible={id === selectedId}
                pinnedDocument={pinnedDoc}
                style={pinnedStyle}
              />
            );

          if (type === 'template')
            return (
              <DocumentTemplateSplit
                key={id}
                visible={id === selectedId}
                pinnedDocument={pinnedDoc}
                patient={patient}
                close={() => close(id)}
                style={pinnedStyle}
              />
            );

          if (type === 'flow')
            return (
              <FlowSplit
                key={id}
                visible={id === selectedId}
                pinnedDocument={pinnedDoc}
                style={pinnedStyle}
                close={() => close(id)}
                bubble={bubble}
              />
            );

          if (type === 'sign_document')
            return (
              <SignDocumentSplit
                key={id}
                visible={id === selectedId}
                pinnedDocument={pinnedDoc}
                patient={patient}
                close={() => close(id)}
                style={pinnedStyle}
              />
            );

          if (type === 'handover')
            return (
              <HandoverSplit
                key={id}
                visible={id === selectedId}
                pinnedDoc={pinnedDoc}
                patient={patient}
                close={() => close(id)}
                style={pinnedStyle}
              />
            );

          if (type === 'referral_request')
            return (
              <ReferralRequestSplit
                key={id}
                visible={id === selectedId}
                referralRequest={pinnedDoc.referralRequest}
                patient={patient}
                close={() => close(id)}
                style={pinnedStyle}
              />
            );

          if (type === 'referral')
            return (
              <ReferralSplit
                key={id}
                visible={id === selectedId}
                text={pinnedDoc.text}
                send_to={pinnedDoc.send_to}
                patient={patient}
                close={() => close(id)}
                style={pinnedStyle}
              />
            );

          if (type === 'webpage')
            return (
              <WebpageSplit
                key={id}
                visible={id === selectedId}
                pinnedDocument={pinnedDoc}
                style={pinnedStyle}
              />
            );
          if (type === 'calculator')
            return (
              <CalculatorSplit
                key={id}
                patient={patient}
                visible={id === selectedId}
                style={pinnedStyle}
                type={pinnedDoc.calculatorType}
              />
            );
          if (type === 'send_multiple_documents') {
            return (
              <PinnedSendMultipleDocuments
                key={id || type}
                visible={id === selectedId}
                style={pinnedStyle}
                patientId={pinnedDoc.patientId}
                onClose={() => close(id)}
              />
            );
          }
          if (type === 'metrics') {
            return (
              <MetricsSplit
                key={id || type}
                visible={id === selectedId}
                style={pinnedStyle}
                patient={patient}
                metric={pinnedDoc.metric}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

const CalculatorSplit = ({ patient, type, visible }) => {
  function getContent() {
    if (type === 'pediatric-dose') return <PediatricDoseCalculator patient={patient} />;
    if (type === 'stop-bang') return <StopBangCalculatorModal patient={patient} />;
    if (type === 'alcohol') return <AlcoholCalculatorModal patient={patient} />;
    if (type === 'pregnancy') return <PregnancyCalculatorModal patient={patient} />;
    if (type === 'ivf') return <IVFCalculatorModal patient={patient} />;
    if (type === 'prevent') return <PREVENTCalculatorModal patient={patient} />;
    if (type === 'fib4') return <FIB4CalculatorModal patient={patient} />;
    if (type === 'lab-price') return <LabPriceCalculator />;
    if (type === 'pack-year') return <PackYearCalculatorModal patient={patient} />;
    if (type === 'bmi') return <BMICalculatorModal patient={patient} />;
    if (type === 'crcl') return <CrClCalculatorModal patient={patient} />;
  }

  let className = 'pinned-document calculator full-height';
  if (!visible) className += ' hide-visibility';
  return <div className={className}>{getContent()}</div>;
};

const PinnedSendMultipleDocuments = ({ patientId, onClose, visible, style }) => {
  const patient = useAppSelector((state) => state.patients.byId[patientId]);

  let className = 'pinned-document send-multiple-documents full-height';
  if (!visible) className += ' hide-visibility';

  return (
    <div className={className} style={style}>
      {patient ? (
        <SendMultipleDocuments patient={patient} onClose={onClose} />
      ) : (
        <div>loading...</div>
      )}
    </div>
  );
};

const MetricsSplit = ({ patient, metric, visible, style }) => {
  let className = 'pinned-document metrics full-height';
  if (!visible) className += ' hide-visibility';

  return (
    <div className={className} style={style}>
      <Graph patient={patient} defaultMetric={metric} />
    </div>
  );
};

const PinnedTab = ({ allPinned, pinnedDocument, selectedId, close, setSelectedId }) => {
  let className = 'tab';
  if (selectedId === pinnedDocument.id) className += ' selected';

  let style = { width: `calc( (100% - 50px) / ${allPinned.length})` };

  const templates = useAppSelector((state) => state.templates.templates) || DEFAULT_ARRAY;
  const patientHr = useAppSelector((state) => state.healthRecords.byId[pinnedDocument.patientId]);

  let payload = useMemo(
    () => getPinnedDocumentPayload(pinnedDocument, templates, patientHr),
    [pinnedDocument, patientHr, templates],
  );

  const title = getName(pinnedDocument, payload);

  return (
    <div className={className} style={style} key={pinnedDocument.id}>
      <Icon className="white" src="xmark" onClick={() => close(pinnedDocument.id)} />
      <span
        className="truncate"
        title={title}
        onClick={() => {
          setSelectedId(pinnedDocument.id);
        }}
      >
        {title}
      </span>
    </div>
  );
};

const WebpageSplit = ({ visible, payload, style }) => {
  let className = 'pinned-document document full-height';
  if (!visible) className += ' hide-visibility';
  return (
    <div className={className} style={style}>
      <iframe title="webpage-split" src={payload.url} />
    </div>
  );
};

const FlowSplit = ({ visible, pinnedDocument, style, close, bubble }) => {
  const { payload, id } = pinnedDocument;
  const dispatch = useAppDispatch();
  let className = 'pinned-document document full-height';
  if (!visible) className += ' hide-visibility';
  const [shouldClose, setShouldClose] = useState(false);

  const patient = useAppSelector((state) => state.patients.byId[pinnedDocument.patientId]);
  const flowFormValues = useAppSelector((state) => state.rhfForms[id]);
  const draftId = payload.draftId;

  useEffect(() => {
    if (!shouldClose) {
      dispatch(setFormValues({ formName: id, values: { ...(flowFormValues || {}), draftId } }));
    }
  }, [dispatch, id, draftId, shouldClose]);

  useEffect(() => {
    if (shouldClose) {
      setTimeout(() => close(), 2500);
    }
  }, [shouldClose]);

  return (
    <div className={className} style={style}>
      <Flow
        patient={patient}
        reason={payload.reason}
        bubble={bubble}
        flow={payload.flow}
        draftId={draftId}
        overrideFormId={id}
        onAccept={(note, records, shouldClose) => {
          if (shouldClose) setShouldClose(true);
          dispatch(
            setFormValues({
              formName: id,
              values: {
                ...(flowFormValues || {}),
                beautifyValues: {
                  note,
                  records,
                  draftId,
                  save: shouldClose,
                },
              },
            }),
          );
        }}
      />
    </div>
  );
};

const ReferralRequestSplit = ({ patient, visible, close, style, referralRequest }) => {
  let className = 'pinned-document referral-request full-height';
  if (!visible) className += ' hide-visibility';
  return (
    <div className={className} style={style}>
      <NewReferralRequest
        patient={patient}
        onSuccess={close}
        referralRequest={referralRequest}
        resetStateOnMount
      />
    </div>
  );
};

const ReferralSplit = ({ patient, visible, close, style, text, send_to }) => {
  let className = 'pinned-document referral full-height';
  if (!visible) className += ' hide-visibility';

  const initialFormState = useMemo(() => {
    return { text, send_to };
  }, [text, send_to]);

  return (
    <div className={className} style={style}>
      <NewReferral
        patient={patient}
        form={CPPUtils.getFormName('referral', patient.id)}
        onClose={close}
        initialFormState={initialFormState}
        text={text}
        send_to={send_to}
      />
    </div>
  );
};

const ReqsListRSplit = ({ pinnedDocument, visible, style, patient }) => {
  const ownPhysician = useAppSelector(ActivePhysician);
  const primaryPhysician = useAppSelector(
    (state) => state.physicians.byId[patient.primary_physician],
  );
  const isNurse = useAppSelector(IsTypeNurse);

  const physician = isNurse ? primaryPhysician : ownPhysician;

  let className = 'pinned-document document full-height';
  if (!visible) className += ' hide-visibility';

  return (
    <div className={className} key={pinnedDocument.type} style={style}>
      <DocumentTemplatesSection patient={patient} physician={physician} />
    </div>
  );
};

const DocumentPDFSplit = ({ pinnedDocument, visible, style }) => {
  const patientHr = useAppSelector((state) => state.healthRecords.byId[pinnedDocument.patientId]);
  let document = useMemo(
    () => getPinnedDocumentPayload(pinnedDocument, undefined, patientHr),
    [pinnedDocument, patientHr],
  );

  const [showModal, setShowModal] = useState(false);

  let className = 'pinned-document document full-height';
  if (!visible) className += ' hide-visibility';

  if (!document) {
    return null;
  }

  return (
    <div className={className} key={document.id} style={style}>
      <DocumentDetailsWrapper
        document={document}
        startShowingDetails={pinnedDocument?.startShowingDetails}
        pinned={true}
        allowPin={false}
        autoLoad={true}
        allowExpand={true}
        fitWidth={true}
        extraButtons={
          !showModal && (
            <DefaultButton
              name="Make Fullscreen"
              icon="expand"
              style={{ width: '49%', marginTop: '10px' }}
              onClick={() => setShowModal(true)}
            />
          )
        }
        showModal={showModal}
        setShowModal={setShowModal}
      />
    </div>
  );
};

const DocumentDetailsWrapper = ({ showModal, setShowModal, ...props }) => {
  return showModal ? (
    <DocumentDetailsModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      {...props}
      pinned={false}
    />
  ) : (
    <DocumentDetails {...props} />
  );
};

const SignDocumentSplit = ({ pinnedDocument, visible, patient, style, close }) => {
  const patientHr = useAppSelector((state) => state.healthRecords.byId[pinnedDocument.patientId]);
  const physician = useAppSelector((state) => state.physicians.byId[pinnedDocument.physicianId]);

  let document = useMemo(
    () => getPinnedDocumentPayload(pinnedDocument, undefined, patientHr),
    [pinnedDocument, patientHr],
  );

  let className = 'pinned-document document full-height';
  if (!visible) className += ' hide-visibility';

  return (
    <div className={className} key={document.id} style={style}>
      <SignDocument
        patient={patient}
        physician={physician}
        document={document}
        onClose={close}
        onSign={close}
      />
    </div>
  );
};

const HandoverSplit = ({ pinnedDoc, visible, style }) => {
  let className = 'pinned-document handover full-height';
  if (!visible) className += ' hide-visibility';

  const handover = pinnedDoc.payload.handover;

  const ownId = useAppSelector((state) => state.authentication.user.id);

  return (
    <div className={className} key={handover.id} style={style}>
      <HandoverDetails
        key={handover.id}
        handover={handover}
        onClose={() => {}}
        editable={false}
        archivable={handover?.physician_id === ownId}
      />
    </div>
  );
};

const DocumentTemplateSplit = ({ pinnedDocument, patient, close, visible, style }) => {
  const dispatch = useAppDispatch();

  const templates = useAppSelector((state) => state.templates.templates) || DEFAULT_ARRAY;
  const patientHr = useAppSelector((state) => state.healthRecords.byId[pinnedDocument.patientId]);

  let {
    id,
    template,
    document,
    noteForm,
    title,
    payload = {},
  } = useMemo(
    () => getPinnedDocumentPayload(pinnedDocument, templates, patientHr),
    [pinnedDocument, patientHr, templates],
  );

  const formData = useAppSelector((state) => state.rhfForms[noteForm]);
  const [showModal, setShowModal] = useState(false);

  const templatesFetchedRef = useRef(false);
  useEffect(() => {
    if (!templatesFetchedRef.current && templates.length === 0) {
      templatesFetchedRef.current = true;
      dispatch(api.Templates.getDocumentTemplates());
    }
  }, [dispatch, templates]);

  const { overrideTitle, defaultSavedDocumentCategory, prefilledValues, overrideId, physician } =
    payload;

  function onSave(body) {
    close();
    if (!!noteForm) {
      let noteValues = formData || {};
      let documents = noteValues.documents || [];
      documents.push(body.id);
      dispatch(setFormValues({ formName: noteForm, values: { ...noteValues, documents } }));
    }
  }

  let className = 'pinned-document template full-width';
  if (!visible) className += ' hide-visibility';

  async function updateDocumentPDF(html, metadata, shouldClose = true) {
    metadata = metadata ? JSON.stringify(metadata) : metadata;

    const result = await dispatch(
      api.Documents.editDocumentFromHtml({
        documentId: document.id,
        hTMLData: {
          html,
          metadata,
        },
        patientId: document.patient_id,
      }),
      {
        errorTitle: 'Edit Document PDF',
      },
    );

    if (result) {
      if (shouldClose) close();
      return result;
    }
  }

  return (
    template?.id && (
      <div className={className} key={template.id} style={style}>
        <DocumentTemplateWrapper
          overrideId={overrideId}
          title={overrideTitle || title}
          onSave={document ? updateDocumentPDF : undefined}
          document={document}
          templateId={template.id}
          onSaveCompleted={onSave}
          onClose={close}
          patient={patient}
          hidePrint={false}
          pinned={true}
          overrideTitle={overrideTitle}
          defaultSavedDocumentCategory={defaultSavedDocumentCategory}
          prefilledValues={prefilledValues}
          showModal={showModal}
          setModal={setShowModal}
          physician={physician}
          extraButtons={
            !showModal && (
              <DefaultButton
                name="Make Fullscreen"
                icon="expand"
                style={{ width: '160px', marginLeft: '10px' }}
                onClick={() => setShowModal(true)}
              />
            )
          }
        />
      </div>
    )
  );
};

const DocumentTemplateWrapper = ({ showModal, setModal, ...props }) => {
  return showModal ? (
    <DocumentTemplateModal
      documentTemplateModal={showModal}
      setDocumentTemplateModal={setModal}
      {...props}
      pinned={false}
      hidePin={false}
      onPinToSide={() => setModal(false)}
    />
  ) : (
    <DocumentTemplate {...props} />
  );
};

/**
 * @param {import('careand-redux/slices/ui/ui').PinnedDocument} pinnedDocument
 * @param {ReturnType<ReturnType<PinnedDocumentPayload>>} payload
 */
function getName(pinnedDocument, payload) {
  const defaultName = 'N/A';

  if (pinnedDocument.type === 'physical_assessment') {
    return 'Physical Assessment';
  }
  if (pinnedDocument.type === 'template') {
    return payload?.tabTitle || payload?.title || defaultName;
  }
  if (pinnedDocument.type === 'reqs_list') {
    return 'R & R & R';
  }
  if (pinnedDocument.type === 'document' || pinnedDocument.type === 'sign_document') {
    return payload?.title || defaultName;
  }
  if (pinnedDocument.type === 'referral_request') {
    return 'Referral Request';
  }
  if (pinnedDocument.type === 'referral') {
    return 'New Referral';
  }
  if (pinnedDocument.type === 'send_multiple_documents') {
    return 'Send Documents';
  }
  if (pinnedDocument.type === 'metrics') {
    return 'Metrics';
  }
  if (pinnedDocument.type === 'calculator') {
    return pinnedDocument.title || 'Calculator';
  }
  if (pinnedDocument.type === 'handover') {
    return 'Handover';
  }
  if (pinnedDocument.type === 'prescription') {
    return 'Prescription';
  }
  return defaultName;
}

function getPinnedDocumentPayload(pinnedDocument, templates, patientHr) {
  const { id, type, payload } = pinnedDocument;
  if (type === 'document' || type === 'sign_document') {
    return patientHr?.documents.find((it) => it.id === id);
  }
  if (type === 'template') {
    let documentId = payload?.document?.id;
    let templateId = payload?.templateId || id;
    const template = templates.find((it) => it.id === templateId);
    const document = patientHr?.documents.find((it) => it.id === documentId);
    return {
      ...pinnedDocument,
      template,
      document,
    };
  }
  return undefined;
}

export const PrescriptionSplit = ({ patientId, prescription, ...rest }) => {
  const dispatch = useAppDispatch();
  const patient = useAppSelector((state) => state.patients.byId[patientId]);
  const form = CPPUtils.getFormName('prescription', patientId);

  useEffect(() => {
    if (prescription) {
      dispatch(setFormValues({ formName: form, values: { ...prescription } }));
    }
  }, [prescription, dispatch, form]);

  return (
    <div className="prescription-split">
      <NewPrescription patient={patient} form={form} onClose={() => {}} />
    </div>
  );
};

export default CPPSplitPanel;
